import React from 'react'
import { Platform, Text, View, type ColorValue } from 'react-native'
import { Redirect, Tabs, usePathname, useSegments } from 'expo-router'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTheme } from '@/lib/hooks/use-theme'
import { useFontScale } from '@/lib/hooks/use-font-scale'
import { useTranslation } from 'react-i18next'
import { Search, Car, Home, User, Map as MapIcon, Wrench } from '@/components/ui/icons'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors, withAlpha } from '@/lib/theme/tokens'
import { impact, ImpactStyle } from '@/lib/utils/haptics'
import { usePendingBookingsCount } from '@/lib/hooks/use-pending-bookings-count'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { AppShell } from '@/components/layout/AppShell'
import { GateFallback } from '@/components/ui/GateFallback'
import { useAuth } from '@/lib/hooks/use-auth'
import { useAuthStore } from '@/lib/stores/auth.store'
import { TabBarInsetProvider } from '@/lib/layout/tab-bar-inset'
import { TopNavInsetProvider } from '@/lib/layout/top-nav-inset'
import { shouldShowMobileTopNav } from '@/lib/navigation/mobile-top-nav'
import { env } from '@/lib/env'
import { isIsolatedPreviewBuild } from '@/lib/runtime-build'

/**
 * Content height of the mobile tab bar, above the home-indicator inset.
 *
 * React Navigation's default is 49 with a 5pt pad inside each item, which put
 * the icons 5pt under the top hairline and left the 16pt label box hard
 * against the bottom edge — on the light capture the label ink ended 3pt from
 * the viewport, and on a device with a home indicator it would sit under it.
 * 64 buys the same air above the icon as below the label (item padding below
 * plus the slack the column leaves at the bottom), and the bar adds
 * `insets.bottom` as its own padding, so the inset is added on top of this.
 */
const TAB_BAR_CONTENT_HEIGHT = SPACING['2xl'] + SPACING.md

/** Label leading, in dp, before the text multiplier is applied. */
const TAB_LABEL_LINE_HEIGHT = 16
/** Glyph box of every tab icon. Not text, so the OS does not scale it. */
const TAB_ICON_SIZE = 20

export default function TabLayout() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  // The bar is the one surface every screen carries, and it was the first to
  // break: a fixed 64dp of chrome around a 12pt label with `minHeight: 16`, so
  // the label box stayed 14.7dp at every text size while the glyphs inside it
  // nearly doubled. Measured on a Pixel 10 Pro XL at font_scale 2.0 the five
  // labels rendered "Explo…", "Viajes", "Mens…", "Anfitr…", "Perfil".
  //
  // Five tabs across 448dp gives each one ~90dp. "Anfitrión" at the size 2.0
  // asks for is ~115dp, so no single line can hold it and no amount of growing
  // the bar sideways will help. The label therefore gets a second line — the
  // whole word, at the size the user chose — and the bar grows to hold it.
  const { box, enlarged } = useFontScale()
  const semantic = useSemanticColors(isDark)
  const { isAuthenticated, isInitializing, isSessionLoading } = useAuth()
  // Held true across the OTP/password/social handoff while the post-auth
  // navigation is in flight. Without this, tabs can mount for one frame with
  // isAuthenticated still false (query subscriber lag) and Redirect to welcome
  // even though verifyOtp just created a live session — the "I entered the
  // code, got bounced to login, then the app knew me after a kill" bug.
  const isCompletingSignup = useAuthStore((s) => s.isCompletingSignup)
  const pendingCount = usePendingBookingsCount()
  const { isDesktop } = useBreakpoint()
  const insets = useSafeAreaInsets()
  const segments = useSegments()
  const pathname = usePathname()
  const routePath = segments.join('/')
  const isProfileNestedRoute = routePath.startsWith('(tabs)/profile/')
  const isConversationRoute = routePath.startsWith('(tabs)/messages/')
  const isHostingWizardRoute =
    routePath.startsWith('(tabs)/hosting/new-listing') ||
    pathname.startsWith('/hosting/new-listing')
  const isExploreFullMapRoute =
    routePath.startsWith('(tabs)/explore/map') || pathname.startsWith('/explore/map')
  const isServiceRequestRoute =
    routePath.startsWith('(tabs)/services/') || pathname.startsWith('/services/')
  const hideMobileTabBar =
    isDesktop ||
    isProfileNestedRoute ||
    isConversationRoute ||
    isHostingWizardRoute ||
    isExploreFullMapRoute ||
    isServiceRequestRoute

  // A bare coloured rectangle is indistinguishable from a frozen app. Same
  // reasoning as the root gate in components/RootLayoutApp.tsx — see
  // components/ui/GateFallback.tsx.
  if (isInitializing || isSessionLoading || isCompletingSignup) {
    return <GateFallback accessibilityLabel={t('common.loading')} testID="gate.tabs" />
  }

  if (!isAuthenticated && !isIsolatedPreviewBuild) {
    return <Redirect href="/(auth)/welcome" />
  }

  // Bar height, derived from what it actually has to hold rather than from a
  // constant that stopped being true the moment the text grew: icon + item
  // padding + the gap React Navigation leaves under the icon + however many
  // scaled label lines this text size needs. `Math.max` keeps the approved
  // 64dp at scale 1 (the derived value is 56 there, so nothing moves).
  const tabLabelLines = enlarged ? 2 : 1
  const tabBarContentHeight = Math.max(
    TAB_BAR_CONTENT_HEIGHT,
    TAB_ICON_SIZE + SPACING.sm * 2 + SPACING.xs + box(TAB_LABEL_LINE_HEIGHT) * tabLabelLines,
  )

  // What the bar occupies below every screen in this group. The bar is a flex
  // SIBLING of the scene, not an overlay, and it pays `insets.bottom` itself —
  // so this is the amount a screen must NOT pay again. Published from here
  // because `hideMobileTabBar` is only knowable here, and synchronously:
  // React Navigation's own height context reports a stale fallback while the
  // bar is `display: 'none'`.
  // The floating material has an 8dp air gap above it. Reserve it here rather
  // than making each screen guess whether a particular tab is visible.
  const tabBarReservation = hideMobileTabBar ? 0 : tabBarContentHeight + insets.bottom + SPACING.xs

  // The global mobile/tablet top bar is a flex sibling of the scene, so the only
  // part a screen could pay twice is the top inset. Published synchronously from
  // here because the predicate is the same one the bar itself consults.
  const hasMobileTopNav = !isDesktop && shouldShowMobileTopNav(pathname)
  const topNavReservation = hasMobileTopNav ? insets.top : 0

  // A render function, not `tabBarLabelStyle`, because only this form can set
  // `numberOfLines`. React Navigation's built-in label is hard-wired to one
  // line with tail truncation, which is what turned "Anfitrión" into
  // "Anfitr…". `title` stays set on every screen below, so the accessible
  // name and the E2E selectors are untouched either way.
  const renderTabLabel =
    (label: string, { primary = false }: { primary?: boolean } = {}) =>
    ({ color }: { color: ColorValue }) => (
      <Text
        numberOfLines={tabLabelLines}
        style={{
          fontFamily: primary ? FONTS.bodyBold : FONTS.body,
          fontSize: 12,
          lineHeight: TAB_LABEL_LINE_HEIGHT,
          // The label is a flex item in the tab button's column, and a flex
          // item shrinks below its content when the column runs out of room.
          // The button measures 47dp on web while icon + gap + a 16dp line box
          // needs 56, so the label was compressed to a 9dp box with
          // `overflow: hidden` over a 16dp line — every label on the bar
          // sliced through the middle of its own letters, in the web app and
          // in the store screenshots taken from it. Measured on
          // app.tago.rent in a signed-in session: height 9px, lineHeight 16px,
          // scrollHeight 16.
          //
          // `tabBarLabelStyle` below has said `minHeight: 16` since
          // 2026-08-01, when this same clip was found and fixed. React
          // Navigation only applies that style to the label it builds itself,
          // so introducing this render function on 2026-08-05 — to stop
          // "Anfitrión" truncating to "Anfitr…" — silently turned the older
          // fix into dead configuration and brought the clip back. Stating it
          // on the element that actually renders is what makes it hold.
          flexShrink: 0,
          height: TAB_LABEL_LINE_HEIGHT * tabLabelLines,
          textAlign: 'center',
          color,
        }}
      >
        {label}
      </Text>
    )

  // A selected destination needs a local, tactile signal—not a full-height
  // coloured strip that makes the glass bar look like five spreadsheet cells.
  // The compact capsule lives only behind the glyph; the readable coral label
  // remains the semantic active-state cue at larger text sizes.
  const renderTabIcon =
    (Icon: typeof Search, { primary = false }: { primary?: boolean } = {}) =>
    ({ color, focused }: { color: ColorValue; focused: boolean }) => {
      const primaryFocused = primary && focused

      return (
        <View
          style={{
            // 28 + 4 gap + 16 label + 16 item padding = 64dp. The primary
            // destination is more visible without making the bar taller or
            // stealing vertical space from the screen below it.
            width: primary ? 42 : 34,
            height: primary ? 28 : 26,
            borderRadius: RADIUS.full,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: primary
              ? primaryFocused
                ? COLORS.coral.DEFAULT
                : withAlpha(COLORS.coral.DEFAULT, isDark ? 0.18 : 0.08)
              : focused
                ? withAlpha(COLORS.coral.DEFAULT, isDark ? 0.22 : 0.12)
                : 'transparent',
            borderWidth: primary || focused ? 1 : 0,
            borderColor: primary
              ? primaryFocused
                ? withAlpha(COLORS.white, 0.28)
                : withAlpha(COLORS.coral.DEFAULT, isDark ? 0.36 : 0.2)
              : focused
                ? withAlpha(COLORS.coral.DEFAULT, isDark ? 0.34 : 0.2)
                : 'transparent',
          }}
        >
          <Icon
            size={primary ? TAB_ICON_SIZE + 1 : TAB_ICON_SIZE}
            color={primaryFocused ? COLORS.white : color}
          />
        </View>
      )
    }

  return (
    <TopNavInsetProvider reservation={topNavReservation}>
      <TabBarInsetProvider reservation={tabBarReservation}>
        <AppShell>
          <Tabs
            screenListeners={{
              tabPress: () => {
                impact(ImpactStyle.Light)
              },
            }}
            screenOptions={{
              headerShown: false,
              ...(Platform.OS === 'android'
                ? { detachInactiveScreens: false, freezeOnBlur: false }
                : {}),
              // One tint paints both the icon and the 12px label, so it has to
              // clear the text minimum (4.5:1), not the icon one (3:1). Raw coral
              // on the light bar measured 3.80:1 — the active tab was the least
              // readable label on the bar. `textAccent` resolves per theme.
              tabBarActiveTintColor: semantic.textAccent,
              tabBarInactiveTintColor: semantic.textMuted,
              tabBarActiveBackgroundColor: 'transparent',
              tabBarInactiveBackgroundColor: 'transparent',
              tabBarBackground: () => (
                <GlassSurface
                  pointerEvents="none"
                  tone="regular"
                  visualRole="chrome"
                  radius={RADIUS.xl + SPACING.xs}
                  style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
                >
                  <></>
                </GlassSurface>
              ),
              // Desktop web → AppShell renders the top nav; hide the mobile tab bar.
              // Mobile/tablet → tab bar spans full viewport (no artificial 800px cap).
              // The label box has to be told to be as tall as its own text.
              //
              // On web the tab label rendered into a div with an explicit
              // `height: 9px; overflow: hidden` while its line box was 16px, so
              // every label was sliced through the descenders — "Explorar",
              // "Viajes", "Mensajes", "Anfitrión", "Perfil". It shipped that way
              // into three of the five screenshots on the live Play listing.
              //
              // Measured, not guessed: walking the DOM in a signed-in session
              // showed height 9px against lineHeight 16px, and overriding to
              // height auto + minHeight 16 rendered all five labels whole.
              //
              // 12px is the floor CLAUDE.md sets for readable type.
              tabBarLabelStyle: {
                fontFamily: FONTS.body,
                fontSize: 12,
                lineHeight: 16,
                height: 'auto',
                minHeight: 16,
              },
              // The item wrapper is what carries the air. React Navigation lays the
              // icon and label out flex-start inside a 5pt-padded column, so the
              // pair hugs the top of the bar and every leftover pixel collects at
              // the bottom; padding here inset the whole pair instead.
              tabBarItemStyle: {
                paddingTop: SPACING.sm,
                paddingBottom: SPACING.sm,
                borderRadius: RADIUS.lg,
                marginHorizontal: 2,
                marginVertical: SPACING.xs,
              },
              tabBarStyle: hideMobileTabBar
                ? { display: 'none' }
                : {
                    backgroundColor: 'transparent',
                    borderTopColor: 'transparent',
                    borderTopWidth: 0,
                    borderRadius: RADIUS.xl + SPACING.xs,
                    marginHorizontal: SPACING.sm,
                    marginTop: SPACING.xs,
                    // `getTabBarHeight` reads a numeric `height` straight off this
                    // style, and the bar adds `insets.bottom` as padding on top of
                    // it — so the inset has to be part of the number or the home
                    // indicator eats the labels.
                    height: tabBarContentHeight + insets.bottom,
                  },
            }}
          >
            <Tabs.Screen
              name="index"
              options={{
                href: null,
              }}
            />
            <Tabs.Screen
              name="trips"
              options={{
                title: t('tabs.trips'),
                tabBarLabel: renderTabLabel(t('tabs.trips')),
                tabBarIcon: renderTabIcon(Car),
                tabBarButtonTestID: 'tab.trips',
              }}
            />
            <Tabs.Screen
              name="services"
              options={{
                title: t('tabs.services'),
                tabBarLabel: renderTabLabel(t('tabs.services')),
                tabBarIcon: renderTabIcon(Wrench),
                tabBarButtonTestID: 'tab.services',
                // El botón vive SIEMPRE: con el flag apagado, `services/_layout`
                // renderiza la ventana "Próximamente" (y también para deep
                // links). Encender el flag publica el funnel sin tocar el orden.
              }}
            />
            <Tabs.Screen
              name="explore"
              options={{
                title: t('tabs.explore'),
                tabBarLabel: renderTabLabel(t('tabs.explore'), { primary: true }),
                tabBarIcon: renderTabIcon(Search, { primary: true }),
                tabBarButtonTestID: 'tab.explore',
              }}
            />
            <Tabs.Screen
              name="tours"
              options={{
                title: t('tabs.tours'),
                tabBarLabel: renderTabLabel(t('tabs.tours')),
                tabBarIcon: renderTabIcon(MapIcon),
                tabBarButtonTestID: 'tab.tours',
                // The catalog is enabled by the release profile. Keeping the
                // route declaration stable means the tab order is identical in
                // Preview and in the production family when it is enabled.
                href: env.isToursEnabled ? undefined : null,
              }}
            />
            <Tabs.Screen
              name="messages"
              options={{
                title: t('tabs.messages'),
                tabBarButtonTestID: 'tab.messages',
                // Five destinations is the maximum that remains comfortably
                // legible at large text. Messages is a high-priority action,
                // not a sixth destination: its unread-aware shortcut lives in
                // the Explore header, while this route remains stable for deep
                // links and conversation back navigation.
                href: null,
              }}
            />
            <Tabs.Screen
              name="hosting"
              options={{
                title: t('tabs.hosting'),
                tabBarLabel: renderTabLabel(t('tabs.hosting')),
                tabBarIcon: renderTabIcon(Home),
                tabBarBadge:
                  pendingCount > 0 ? (pendingCount > 99 ? '99+' : pendingCount) : undefined,
                tabBarButtonTestID: 'tab.hosting',
              }}
            />
            <Tabs.Screen
              name="profile"
              options={{
                title: t('tabs.profile'),
                tabBarLabel: renderTabLabel(t('tabs.profile')),
                tabBarIcon: renderTabIcon(User),
                tabBarButtonTestID: 'tab.profile',
                // Profile lives in the top avatar (Explore mobile, AppTopNav
                // desktop). Hidden from the bottom bar but kept as a route so
                // router.push('/profile') still works.
                href: null,
              }}
            />
          </Tabs>
        </AppShell>
      </TabBarInsetProvider>
    </TopNavInsetProvider>
  )
}
